import { useState, type ComponentType } from 'react'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import {
  Bot,
  BookOpen,
  Building2,
  ChevronsUpDown,
  Contact,
  LayoutDashboard,
  LogOut,
  Menu,
  MessageSquare,
  Moon,
  Phone,
  Settings,
  ShieldCheck,
  Store,
  Sun,
  User,
  Users,
} from 'lucide-react'
import type { SociedadRank } from '../api'
import { useAuth } from '../AuthContext'
import { useTheme } from '../useTheme'
import { RANK_LABELS } from '../lib/rank'
import { UserAvatar } from '../components/UserAvatar'
import { Button } from '../components/ui/button'
import { Sheet, SheetContent, SheetTitle } from '../components/ui/sheet'
import { Separator } from '../components/ui/separator'
import { Badge } from '../components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu'

interface NavItem {
  to: string
  label: string
  icon: ComponentType<{ className?: string }>
  end?: boolean
}

const ADMIN_NAV: NavItem[] = [
  { to: '/admin', label: 'Resumen', icon: LayoutDashboard, end: true },
  { to: '/admin/empresas', label: 'Empresas', icon: Building2 },
  { to: '/admin/sociedades', label: 'Sociedades', icon: Store },
  { to: '/admin/super-admins', label: 'Super Admins', icon: ShieldCheck },
]

function tenantNav(rank: SociedadRank | undefined): NavItem[] {
  const base: NavItem[] = [{ to: '/app/conversaciones', label: 'Conversaciones', icon: MessageSquare }]
  if (rank === 'operador' || !rank) return base
  const perSociedadNav: NavItem[] = [
    ...base,
    { to: '/app/whatsapp', label: 'Números de WhatsApp', icon: Phone },
    { to: '/app/base-de-conocimiento', label: 'Base de conocimiento', icon: BookOpen },
  ]
  if (rank !== 'owner') {
    // El Administrador sí trabaja Sociedad por Sociedad (con el switcher) —
    // "Miembros" (crear/gestionar Operadores de la Sociedad activa) le sigue
    // haciendo falta acá.
    return [...perSociedadNav, { to: '/app/miembros', label: 'Miembros', icon: Users }]
  }
  // El Owner ve todo consolidado: "Administradores" ya incluye crear y
  // gestionar Operadores de TODAS sus Sociedades — no necesita "Miembros"
  // (que está scopeado a una Sociedad puntual) en su menú. "Dashboard" va
  // primero — es la vista agregada de todas sus Sociedades, el punto de
  // entrada natural para el Propietario.
  return [
    { to: '/app/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    ...perSociedadNav,
    { to: '/app/contactos', label: 'Contactos', icon: Contact },
    { to: '/app/sociedades', label: 'Sociedades', icon: Store },
    { to: '/app/administradores', label: 'Administradores', icon: ShieldCheck },
  ]
}

function SidebarNav({ items, onNavigate }: { items: NavItem[]; onNavigate?: () => void }) {
  return (
    <nav className="flex flex-col gap-0.5 px-2">
      {items.map(({ to, label, icon: Icon, end }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          onClick={onNavigate}
          className={({ isActive }) =>
            `flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm font-medium transition-colors ${
              isActive
                ? 'bg-sidebar-primary text-sidebar-primary-foreground'
                : 'text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
            }`
          }
        >
          <Icon className="size-4 shrink-0" />
          {label}
        </NavLink>
      ))}
    </nav>
  )
}

function UserMenu({ mode, onNavigate }: { mode: 'admin' | 'tenant'; onNavigate?: () => void }) {
  const { user, activeSociedadId, logout } = useAuth()
  const navigate = useNavigate()
  if (!user) return null

  const activeMembership = user.sociedades.find((s) => s.id === activeSociedadId)
  const roleLine = mode === 'admin' ? 'SuperAdmin de plataforma' : activeMembership ? RANK_LABELS[activeMembership.rank] : null

  function go(path: string): void {
    onNavigate?.()
    navigate(path)
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left text-sm font-medium text-sidebar-foreground/90 hover:bg-sidebar-accent"
        >
          <UserAvatar name={user.nombre} seed={user.email} size="sm" />
          <span className="min-w-0 flex-1 truncate">{user.nombre}</span>
          <ChevronsUpDown className="size-3.5 shrink-0 text-sidebar-foreground/50" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" side="top" className="w-64">
        <DropdownMenuLabel className="flex flex-col gap-0.5 font-normal">
          <span className="truncate text-sm font-medium">{user.nombre}</span>
          <span className="truncate text-xs text-muted-foreground">{user.email}</span>
          {roleLine && (
            <Badge variant="outline" className="mt-1 w-fit gap-1 text-[11px]">
              {roleLine}
            </Badge>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => go(mode === 'admin' ? '/admin/perfil' : '/app/perfil')}>
          <User />
          Perfil
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => go(mode === 'admin' ? '/admin/settings' : '/app/settings')}>
          <Settings />
          Settings
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem variant="destructive" onSelect={() => logout()}>
          <LogOut />
          Cerrar sesión
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function SidebarContent({ mode, onNavigate }: { mode: 'admin' | 'tenant'; onNavigate?: () => void }) {
  const { user, activeSociedadId } = useAuth()
  const activeMembership = user?.sociedades.find((s) => s.id === activeSociedadId)
  const items = mode === 'admin' ? ADMIN_NAV : tenantNav(activeMembership?.rank)
  const canCrossLink = mode === 'admin' ? (user?.sociedades.length ?? 0) > 0 : !!user?.is_admin

  return (
    <div className="flex h-full flex-col bg-sidebar text-sidebar-foreground">
      <div className="flex items-center gap-2 px-4 py-4">
        <div className="flex size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
          <Bot className="size-4.5" />
        </div>
        <span className="text-sm font-semibold tracking-tight">ChatBot MCW</span>
      </div>

      {mode === 'admin' && (
        <div className="px-4 pb-3">
          <Badge variant="secondary" className="gap-1.5">
            <ShieldCheck className="size-3" />
            Panel de plataforma
          </Badge>
        </div>
      )}

      <Separator className="bg-sidebar-border" />

      <div className="flex-1 overflow-y-auto py-3">
        <SidebarNav items={items} onNavigate={onNavigate} />
      </div>

      <Separator className="bg-sidebar-border" />

      <div className="flex flex-col gap-1 p-2">
        {canCrossLink && (
          <NavLink
            to={mode === 'admin' ? '/app/conversaciones' : '/admin'}
            onClick={onNavigate}
            className="flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm font-medium text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
          >
            {mode === 'admin' ? <MessageSquare className="size-4" /> : <ShieldCheck className="size-4" />}
            {mode === 'admin' ? 'Volver a mi cuenta' : 'Panel de plataforma'}
          </NavLink>
        )}
        <UserMenu mode={mode} onNavigate={onNavigate} />
      </div>
    </div>
  )
}

export function AppShell({ mode }: { mode: 'admin' | 'tenant' }) {
  const [mobileOpen, setMobileOpen] = useState(false)
  const { theme, toggle } = useTheme()

  return (
    <div className="flex h-screen overflow-hidden">
      <aside className="hidden w-64 shrink-0 border-r border-sidebar-border md:block">
        <div className="fixed h-screen w-64">
          <SidebarContent mode={mode} />
        </div>
      </aside>

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-64 p-0">
          <SheetTitle className="sr-only">Navegación</SheetTitle>
          <SidebarContent mode={mode} onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between gap-3 border-b border-border px-4 py-3 md:px-6">
          <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setMobileOpen(true)} aria-label="Abrir menú">
            <Menu className="size-5" />
          </Button>

          <Button variant="ghost" size="icon" onClick={toggle} aria-label="Cambiar tema" className="ml-auto">
            {theme === 'dark' ? <Sun className="size-4" /> : <Moon className="size-4" />}
          </Button>
        </header>

        <main className="min-h-0 flex-1 overflow-y-auto p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
